import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import { Contrast, DarkMode, WbSunny } from '@mui/icons-material'
import type { ThemeMode } from '@/common/theme'
import { useTheme } from '@/common/theme'
import { sectionDescriptionSx, sectionTitleSx } from './settingsSectionStyles'

const THEME_OPTIONS: readonly ThemeOption[] = [
  { mode: 'light', label: 'Light', Icon: WbSunny },
  { mode: 'dark', label: 'Dark', Icon: DarkMode },
  { mode: 'system', label: 'System', Icon: Contrast },
]

interface ThemeOption {
  readonly mode: ThemeMode
  readonly label: string
  readonly Icon: typeof WbSunny
}

export const AppearanceSection: FC = () => {
  const { mode, setMode } = useTheme()

  const handleSelect = useCallback(
    (selectedMode: ThemeMode) => {
      setMode(selectedMode)
    },
    [setMode],
  )

  return (
    <Box>
      <Typography variant="body1" sx={sectionTitleSx}>
        Appearance
      </Typography>
      <Typography variant="body2" sx={sectionDescriptionSx}>
        Choose your interface theme.
      </Typography>
      <Box sx={optionsContainerSx}>
        {THEME_OPTIONS.map((option) => (
          <ThemeOptionButton
            key={option.mode}
            option={option}
            isSelected={mode === option.mode}
            onSelect={handleSelect}
          />
        ))}
      </Box>
    </Box>
  )
}

interface ThemeOptionButtonProps {
  readonly option: ThemeOption
  readonly isSelected: boolean
  readonly onSelect: (mode: ThemeMode) => void
}

const ThemeOptionButton: FC<ThemeOptionButtonProps> = ({ option, isSelected, onSelect }) => {
  const { mode, label, Icon } = option

  const handleClick = useCallback(() => {
    onSelect(mode)
  }, [mode, onSelect])

  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={optionButtonSx(isSelected)}
      aria-label={label}
      aria-pressed={isSelected}
    >
      <Icon sx={optionIconSx(isSelected)} />
      <Typography variant="caption" sx={optionLabelSx(isSelected)}>
        {label}
      </Typography>
    </Box>
  )
}

const optionsContainerSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
}

const optionButtonSx = (isSelected: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
  px: 2,
  py: 1.5,
  borderRadius: 1.5,
  border: 'none',
  backgroundColor: isSelected ? 'action.selected' : 'transparent',
  cursor: 'pointer',
  transition: 'all 150ms ease-in-out',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
})

const optionIconSx = (isSelected: boolean): SxProps<Theme> => ({
  fontSize: 18,
  color: isSelected ? 'text.primary' : 'text.disabled',
  opacity: isSelected ? 1 : 0.7,
})

const optionLabelSx = (isSelected: boolean): SxProps<Theme> => ({
  color: isSelected ? 'text.primary' : 'text.disabled',
  fontSize: '0.7rem',
  fontWeight: isSelected ? 500 : 400,
})
